import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  Fragment,
  lazy,
  Suspense,
  type FormEvent,
  type KeyboardEvent,
  type ChangeEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";
import {
  dateienAnnehmen,
  ablehnungsText,
  nachrichtBauen,
  type PendingFile,
} from "../lib/pending-files";
import { useChat, cachePendingPlaintext, cacheOwnMessage, type Message } from "../hooks/useChat";
import { useCrypto, RecipientNotReadyError, EmptyGroupError, CryptoBrokenError, type MultiDeviceRecipient, type EncryptFanoutMeta } from "../hooks/useCrypto";
import { ws } from "../lib/ws";
import { api, ApiError } from "../lib/api";
import { expectedDevicesHash } from "../lib/hash";
import { Avatar } from "./Avatar";
import { ChannelInfo } from "./ChannelInfo";
import { MessageContextMenu, type ContextMenuAction } from "./MessageContextMenu";
import { MessageBubble, getBubbleColor } from "./MessageBubble";
import { ImageLightbox } from "./ImageLightbox";
import { MessageMarkdown } from "./MessageMarkdown";
import { ForwardDialog } from "./ForwardDialog";
import { SearchBar } from "./SearchBar";
import { MessageInput } from "./MessageInput";
import { ChevronLeft, HelpCircle, Search, Info, Paperclip, Reply, Copy, Forward, Pencil, Trash2, Phone, Loader2, Download, Smile } from "lucide-react";

const EmojiPicker = lazy(() => import("./EmojiPicker").then(m => ({ default: m.EmojiPicker })));
import { useAuth } from "../hooks/useAuth";
import { exportConversationAsHtml } from "../lib/conversation-export";
import { HelpDialog } from "./HelpDialog";
import { VoiceChatMode } from "./VoiceChatMode";
import type { FileUploadResult } from "../types/file";
import { safeMessageText } from "../lib/safeMessageText";
import styles from "./ChatView.module.css";

interface ChatViewProps {
  channelId: string;
  currentUserId: string;
  onBack?: () => void;
}

export function ChatView({ channelId, currentUserId, onBack }: ChatViewProps) {
  const { messages: allMessages, channels, typingUsers, readReceipts, sendReadReceipt, onlineUsers, lastSeenMap, loadMessages, addMessage, removeMessage, botStreams, botTyping, botActivity, connectionState, isLoadingMessages, hasMoreMessages } = useChat();
  const { encryptForPeer, encryptForGroup, encryptForAllDevices, initialized: e2eeReady } = useCrypto();
  const messages = allMessages.get(channelId) ?? [];
  const channel = channels.find((c) => c.id === channelId);
  const typing = typingUsers.get(channelId) ?? [];
  const { user } = useAuth();
  const isAdmin = user?.orgRole === "owner" || user?.orgRole === "admin";
  const channelReceipts = readReceipts.get(channelId);

  const [inputText, setInputText] = useState("");
  const [showInfo, setShowInfo] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  // KOL-108: Liste statt Einzeldatei. Der Nachrichtentext trägt seit KOL-90
  // beliebig viele [efile:…]-Token; nur der Composer konnte bisher nur eine.
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [uploadHinweis, setUploadHinweis] = useState<string | null>(null);
  const [showVoiceChat, setShowVoiceChat] = useState(false);

  // Scroll state
  const [showNewMessageBtn, setShowNewMessageBtn] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const prevMessageCountRef = useRef(messages.length);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    message: Message;
  } | null>(null);
  const menuPosRef = useRef({ x: 0, y: 0 });

  // Reaction picker (opened via "Reagieren" context-menu action)
  const [reactionPicker, setReactionPicker] = useState<{
    x: number;
    y: number;
    message: Message;
  } | null>(null);

  // Edit mode
  const [editingMessage, setEditingMessage] = useState<Message | null>(null);
  // Help dialog state
  const [showHelp, setShowHelp] = useState(false);

  // Reply mode
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  // Forward mode
  const [forwardMessage, setForwardMessage] = useState<Message | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  // Search
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Reconnect banner
  // Banner state: "hidden" | "disconnected" | "reconnected"
  const [banner, setBanner] = useState<"hidden" | "disconnected" | "reconnected">("hidden");
  const disconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track whether we ever had a successful connection — suppress banner until first connect
  const hasConnectedRef = useRef(false);

  useEffect(() => {
    if (connectionState === "connected") {
      hasConnectedRef.current = true;
      // Cancel pending disconnect timer
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
      // Show "Verbunden" only if user saw the disconnect banner
      setBanner((cur) => {
        if (cur === "disconnected") {
          if (reconnectedTimerRef.current) clearTimeout(reconnectedTimerRef.current);
          reconnectedTimerRef.current = setTimeout(() => setBanner("hidden"), 2000);
          return "reconnected";
        }
        return "hidden";
      });
    } else if (connectionState === "reconnecting" || connectionState === "disconnected") {
      // Only show disconnect banner AFTER we had a successful connection
      // (suppresses banner during initial page load / first connect)
      if (hasConnectedRef.current && !disconnectTimerRef.current) {
        disconnectTimerRef.current = setTimeout(() => {
          disconnectTimerRef.current = null;
          setBanner("disconnected");
        }, 3000);
      }
    }
  }, [connectionState]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (disconnectTimerRef.current) clearTimeout(disconnectTimerRef.current);
      if (reconnectedTimerRef.current) clearTimeout(reconnectedTimerRef.current);
    };
  }, []);

  // Drag & Drop
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // Inner content wrapper — observed by ResizeObserver to drive sticky-bottom (F12).
  // Its height equals the sum of message bubble heights, so any async growth
  // (E2EE decrypt re-render, image load, streaming chunk) fires the observer.
  const messagesContentRef = useRef<HTMLDivElement>(null);
  // True while the viewport should auto-snap to the bottom on content growth.
  // Flipped by handleScroll (user scrolled up → false; back at bottom → true) and
  // by scrollToBottom / channel change. Lives in a ref so the ResizeObserver
  // callback can read the current value without re-binding.
  const stickToBottomRef = useRef(true);
  // Last observed scrollTop — lets handleScroll tell a deliberate upward user
  // scroll (detach from bottom) apart from a scroll event caused by below-fold
  // content growth (async attachment / link-preview load), which must NOT detach.
  const lastScrollTopRef = useRef(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Scroll Logic ──

  const checkIfNearBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    const threshold = 150;
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  }, []);

  const scrollToBottom = useCallback((smooth = true) => {
    stickToBottomRef.current = true;
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? "smooth" : "instant" });
    setShowNewMessageBtn(false);
    setIsNearBottom(true);
  }, []);

  // Handle scroll events to track position
  // Update own-bubble gradient colors based on viewport Y position
  const updateBubbleColors = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const bubbles = container.querySelectorAll<HTMLElement>(`.${styles.ownBubble}`);
    for (const bubble of bubbles) {
      const rect = bubble.getBoundingClientRect();
      // Position ratio within the scroll container viewport (0 = top, 1 = bottom)
      const ratio = Math.max(0, Math.min(1,
        (rect.top - containerRect.top) / (containerRect.height || 1)
      ));
      bubble.style.setProperty("--bubble-color", getBubbleColor(ratio));
    }
  }, []);

  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    const curTop = container ? container.scrollTop : 0;
    const prevTop = lastScrollTopRef.current;
    lastScrollTopRef.current = curTop;

    const near = checkIfNearBottom();
    setIsNearBottom(near);

    // Sticky semantics (hardened): ONLY a deliberate upward user scroll detaches
    // from the bottom. Scroll events triggered by below-fold content growth leave
    // scrollTop unchanged (or the ResizeObserver pin pushes it down), so they must
    // not flip sticky off — otherwise an async PDF/image/link-preview load kicks
    // the viewport off the bottom onto older messages. Returning to the bottom
    // always re-arms sticky.
    const scrolledUp = curTop < prevTop - 2;
    if (near) {
      stickToBottomRef.current = true;
    } else if (scrolledUp) {
      stickToBottomRef.current = false;
    }
    // Show "Neue Nachricht" only when actually detached and away from bottom —
    // not while sticky is still pinning us back down.
    setShowNewMessageBtn(!stickToBottomRef.current && !near);
    updateBubbleColors();

    // Infinite scroll: load older messages when near top
    if (container && container.scrollTop < 100 && hasMoreMessages && !isLoadingMessages && messages.length > 0) {
      const oldestMsg = messages[0];
      if (oldestMsg) {
        // Save scroll position to restore after prepending
        const prevScrollHeight = container.scrollHeight;
        loadMessages(channelId, oldestMsg.createdAt).then(() => {
          // Restore scroll position after older messages are prepended
          requestAnimationFrame(() => {
            if (messagesContainerRef.current) {
              const newScrollHeight = messagesContainerRef.current.scrollHeight;
              messagesContainerRef.current.scrollTop = newScrollHeight - prevScrollHeight;
            }
          });
        });
      }
    }
  }, [checkIfNearBottom, updateBubbleColors, hasMoreMessages, isLoadingMessages, messages, loadMessages, channelId]);

  // Initial color update after messages render
  useEffect(() => {
    requestAnimationFrame(updateBubbleColors);
  }, [messages, updateBubbleColors]);

  // When new messages arrive
  useEffect(() => {
    const newCount = messages.length;
    const oldCount = prevMessageCountRef.current;
    prevMessageCountRef.current = newCount;

    if (newCount <= oldCount) return; // no new messages

    // Initial load (from 0 → N messages) — always scroll to bottom
    if (oldCount === 0) {
      scrollToBottom(false);
      return;
    }

    const latestMsg = messages[messages.length - 1];
    const isOwnMessage = latestMsg?.senderId === currentUserId;

    // Always scroll to bottom for:
    // - Own messages (obvious)
    // - Already near bottom (user hasn't scrolled up)
    // - Bot replies in DM channels (user was watching the stream,
    //   expects the final message to appear in view)
    const isDmWithBot = channel?.type === "dm";
    const isBotReply = !isOwnMessage && isDmWithBot;

    // stickToBottomRef.current = the user's standing "stay at bottom" intent,
    // which now survives below-fold content growth (see handleScroll). Honour it
    // alongside isNearBottom so a pinned user is always carried to a new message.
    if (isOwnMessage || stickToBottomRef.current || isNearBottom || isBotReply) {
      scrollToBottom();
    } else {
      // Show "Neue Nachricht" button
      setShowNewMessageBtn(true);
    }
  }, [messages.length, currentUserId, isNearBottom, scrollToBottom, messages]);

  // (Auto-scroll during bot streaming is now driven by the ResizeObserver below
  //  — every chunk grows the streaming bubble, observer fires, sticky snaps.)

  // F12: sticky-bottom via ResizeObserver. Replaces the previous "fire forceScroll
  // 4× on channel change (immediate / RAF / 100ms / 300ms)" hack which lost the
  // race against async decrypts and image loads on hard reload (S11).
  // The observer fires for every height change of the message-list wrapper, so
  // late-arriving decrypts/images keep the viewport pinned to the bottom as long
  // as the user hasn't scrolled away.
  useEffect(() => {
    const container = messagesContainerRef.current;
    const content = messagesContentRef.current;
    if (!container || !content) return;
    let rafId: number | null = null;
    const observer = new ResizeObserver(() => {
      if (!stickToBottomRef.current) return;
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (stickToBottomRef.current) {
          container.scrollTop = container.scrollHeight;
        }
      });
    });
    observer.observe(content);
    return () => {
      observer.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  // Reset state on channel change. Sticky is re-armed and we do one immediate
  // snap so the initial paint already shows the bottom; the ResizeObserver then
  // keeps re-snapping as messages decrypt and images load.
  // Don't auto-focus input on mobile (opens keyboard immediately)
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) inputRef.current?.focus();
    setShowNewMessageBtn(false);
    setEditingMessage(null);
    setReplyTo(null);
    setShowEmojiPicker(false);
    prevMessageCountRef.current = 0; // Reset so initial-load detection works

    stickToBottomRef.current = true;
    setIsNearBottom(true);
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
      // Seed the scroll baseline so the first handleScroll after a channel switch
      // doesn't read a stale prevTop and misfire the upward-scroll detection.
      lastScrollTopRef.current = container.scrollTop;
    }
  }, [channelId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-send read receipt for last message when viewing channel
  useEffect(() => {
    if (messages.length === 0) return;
    const lastMsg = messages[messages.length - 1]!;
    // Always send receipt — marks channel as read on server (affects unread count on reload)
    sendReadReceipt(channelId, lastMsg.id);
  }, [channelId, messages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup preview URLs
  useEffect(() => {
    return () => {
      for (const p of pendingFiles) {
        if (p.preview) URL.revokeObjectURL(p.preview);
      }
    };
  }, [pendingFiles]);

  // ── Message Send ──

  /** Send an encrypted result via the appropriate channel (WS for legacy, HTTP for multi-device) */
  const sendEncryptedMessage = async (
    result: EncryptResult,
    plaintext: string,
    options?: { replyToId?: string; contentType?: string },
  ) => {
    if (result.mode === "multi-device") {
      const postRecipients = async (recipients: MultiDeviceRecipient[]) => {
        // Fix 1: on a partial send we KNOW the recipient set is incomplete, so we
        // must NOT send X-Expected-Devices — otherwise the server 409s on the
        // intentionally-missing device slots. Backfill heals them afterwards.
        const hash = result.partial ? null : await expectedDevicesHash(recipients);
        return api.post<{ message: { id: string } }>(
          `/channels/${channelId}/messages`,
          {
            recipients,
            contentType: options?.contentType ?? "e2ee",
            replyToId: options?.replyToId,
            // Fix 2: tell the server this recipient set is intentionally incomplete
            // so it enqueues async backfill for the uncovered peer devices.
            ...(result.partial ? { partial: true } : {}),
            // Plaintext failsafe sent WITH the message so the server writes the
            // channel_shared mirror before broadcasting → a bot recipient that
            // can't decrypt recovers it without racing the post-send backfill.
            // Server gates on DEBUG_BACKUP_PLAINTEXT_ENABLED + allow-list.
            debugPlaintext: plaintext,
          },
          hash ? { headers: { "X-Expected-Devices": hash } } : undefined,
        );
      };

      let response: { message: { id: string } } | null = null;
      try {
        response = await postRecipients(result.recipients);
      } catch (err) {
        // Server rejects when a recipient deviceId is unknown (BUG 2 server fix).
        // Re-encrypt with a fresh /users/keys snapshot and retry once. Covers the
        // race where the peer rotated/removed a device between our key fetch and
        // this POST.
        if (err instanceof ApiError && err.status === 400 && err.code === "unknown_recipient_device") {
          console.warn("[Send] Recipient device unknown, refreshing keys and retrying once");
          const fresh = await encryptMessage(plaintext);
          if (fresh.mode === "multi-device") {
            response = await postRecipients(fresh.recipients);
          } else {
            // After refresh the channel resolves to a non-multi-device path
            // (e.g. plaintext bot fallback) — hand off to the normal pipeline.
            await sendEncryptedMessage(fresh, plaintext, options);
            return;
          }
        } else if (err instanceof ApiError && err.status === 409 && err.code === "mismatched_devices") {
          // SKDM Phase 3b: server's device view differs from ours. Re-encrypt
          // with a fresh /users/keys snapshot (which now reflects the server-side
          // change) and retry once.
          console.warn("[Send] Device-set mismatch, refreshing keys and retrying once");
          const fresh = await encryptMessage(plaintext);
          if (fresh.mode === "multi-device") {
            response = await postRecipients(fresh.recipients);
          } else {
            await sendEncryptedMessage(fresh, plaintext, options);
            return;
          }
        } else {
          throw err;
        }
      }

      // Cache plaintext and optimistically add message to chat (don't wait for WS echo)
      if (response?.message?.id) {
        cacheOwnMessage(response.message.id, plaintext, channelId);
        // Upload encrypted backup so own-device peers (e.g. Firefox when Safari sent)
        // can fall back to tryBackupDecrypt() if their E2EE session was reset.
        useCrypto.getState().uploadMessageBackup(response.message.id, plaintext);
        addMessage({
          id: response.message.id,
          channelId,
          senderId: currentUserId,
          senderName: channel?.members?.find(m => m.userId === currentUserId)?.displayName ?? "",
          ciphertext: "",
          plaintext,
          contentType: "e2ee",
          createdAt: new Date().toISOString(),
        });
      }
    } else if (result.mode === "plaintext") {
      // Not encrypted — send via WS as plaintext (HTTP fallback below if WS blocked)
      if (!ws.connected) {
        await sendViaHttpFallback(result.ciphertext, "text", plaintext, options?.replyToId);
        return;
      }
      if (options?.replyToId) {
        ws.send({ type: "send_message", channelId, ciphertext: result.ciphertext, contentType: "text", replyToId: options.replyToId });
      } else {
        ws.sendMessage(channelId, result.ciphertext, "text");
      }
    } else {
      // Legacy or group — single ciphertext via WS (HTTP fallback if WS blocked)
      const ct = result.mode === "legacy" || result.mode === "group" ? "e2ee" : "text";
      cachePendingPlaintext(result.ciphertext, plaintext);
      if (!ws.connected) {
        await sendViaHttpFallback(result.ciphertext, ct, plaintext, options?.replyToId);
        return;
      }
      if (options?.replyToId) {
        ws.send({ type: "send_message", channelId, ciphertext: result.ciphertext, contentType: ct, replyToId: options.replyToId });
      } else {
        ws.sendMessage(channelId, result.ciphertext, ct);
      }
    }
  };

  /**
   * HTTP-Sendepfad für group/legacy/plaintext, wenn der WebSocket nicht
   * verbunden ist (Firmen-Proxy blockt WSS — KOL-27). Nutzt denselben
   * Server-Endpoint wie der Multi-Device-Pfad; ohne recipients landet der
   * Request im Legacy-Einzel-Ciphertext-Zweig (channel.routes.ts).
   * Ohne WS kommt kein Echo — Message optimistisch aus der Response einfügen.
   */
  const sendViaHttpFallback = async (
    ciphertext: string,
    contentType: string,
    plaintext: string,
    replyToId?: string,
  ) => {
    const response = await api.post<{ message: { id: string } }>(
      `/channels/${channelId}/messages`,
      { ciphertext, contentType, replyToId },
    );
    if (response?.message?.id) {
      cacheOwnMessage(response.message.id, plaintext, channelId);
      useCrypto.getState().uploadMessageBackup(response.message.id, plaintext);
      addMessage({
        id: response.message.id,
        channelId,
        senderId: currentUserId,
        senderName: channel?.members?.find((m) => m.userId === currentUserId)?.displayName ?? "",
        ciphertext,
        plaintext,
        contentType,
        createdAt: new Date().toISOString(),
      });
    }
  };

  const handleSend = async (e: FormEvent) => {
    e.preventDefault();
    const text = inputText.trim();

    // Handle edit mode
    if (editingMessage) {
      if (text && text !== safeMessageText(editingMessage)) {
        const encrypted = await encryptMessage(text);
        // Edit only supports single ciphertext (legacy path)
        const ciphertext = encrypted.mode === "multi-device"
          ? (await (async () => { /* fallback to legacy for edits */ return (await encryptForPeer(
              channel?.members?.find((m) => m.userId !== currentUserId)?.userId ?? "",
              text,
            )); })())
          : encrypted.ciphertext;
        ws.send({
          type: "edit_message",
          channelId,
          messageId: editingMessage.id,
          ciphertext,
        });
        // The edit ciphertext is encrypted for the peer only, so the echoed
        // message_edit can't be self-decrypted. Without refreshing the own
        // plaintext cache + bubble here, the sender keeps seeing the pre-edit
        // text (ownMessageCache short-circuit in useChat message_edit handler).
        cacheOwnMessage(editingMessage.id, text, channelId, /* force re-backup */ true);
        useChat.getState().updateMessage(editingMessage.id, channelId, { ciphertext, plaintext: text });
      }
      setEditingMessage(null);
      setInputText("");
      return;
    }

    // Handle file upload (client-side encrypted)
    if (pendingFiles.length > 0) {
      setUploading(true);
      try {
        // KOL-108: nacheinander statt gleichzeitig. Langsamer, dafür lässt sich
        // ehrlich anzeigen, wo wir stehen, und ein Fehler trifft genau eine Datei.
        const { encryptFile, buildEncryptedFileToken } = await import("../lib/file-crypto");
        const tokens: string[] = [];
        const gescheitert: PendingFile[] = [];

        for (const [i, ausstehend] of pendingFiles.entries()) {
          setUploadHinweis(pendingFiles.length > 1 ? `Datei ${i + 1} von ${pendingFiles.length}…` : null);
          try {
            // Step 1: Encrypt the file client-side with AES-256-GCM
            const fileBuffer = await ausstehend.file.arrayBuffer();
            const { encrypted: encryptedBlob, key: fileKey, iv: fileIv } = await encryptFile(fileBuffer);

            // Step 2: Upload the encrypted blob (server only sees ciphertext)
            const encryptedFile = new File(
              [encryptedBlob],
              ausstehend.file.name,
              { type: "application/octet-stream" },
            );
            const result = await api.upload<FileUploadResult>(`/files/upload?channelId=${channelId}`, encryptedFile);

            // Step 3: Embed the AES key+IV in the E2EE message plaintext
            // Only the E2EE recipient can read the key → only they can decrypt the file
            const originalMime = ausstehend.file.type || "application/octet-stream";
            const fileName = result.filename ?? ausstehend.file.name;
            tokens.push(buildEncryptedFileToken(result.fileId, originalMime, fileName, fileKey, fileIv));
          } catch (err) {
            // Eine gescheiterte Datei darf die übrigen nicht mitnehmen: was
            // hochgeladen ist, geht raus, der Rest bleibt sichtbar im Composer.
            console.error("Upload failed:", ausstehend.file.name, err);
            gescheitert.push(ausstehend);
          }
        }
        setUploadHinweis(null);

        if (tokens.length > 0) {
          const fileMsg = nachrichtBauen(text, tokens);
          const encrypted = await encryptMessage(fileMsg);
          await sendEncryptedMessage(encrypted, fileMsg, { contentType: "e2ee" });
        }

        if (gescheitert.length > 0) {
          for (const p of pendingFiles) {
            if (!gescheitert.includes(p) && p.preview) URL.revokeObjectURL(p.preview);
          }
          setPendingFiles(gescheitert);
          alert(
            `Nicht hochgeladen: ${gescheitert.map((p) => p.file.name).join(", ")}. ` +
              `Die übrigen ${tokens.length} sind gesendet — die gescheiterten bleiben im Eingabefeld.`,
          );
          setUploading(false);
          return;
        }
      } catch (err) {
        console.error("Upload failed:", err);
        if (err instanceof CryptoBrokenError) {
          alert("Dieses Gerät hat einen beschädigten Verschlüsselungs-Zustand. Bitte lade die Seite neu oder melde dich ab und wieder an — bis dahin können keine Nachrichten gesendet werden.");
        } else if (err instanceof RecipientNotReadyError) {
          alert("Empfänger ist noch nicht vollständig eingerichtet. Bitte später erneut versuchen.");
        } else if (err instanceof EmptyGroupError) {
          alert("Diese Gruppe hat noch keine anderen Mitglieder. Füge zuerst jemanden hinzu, bevor du eine Datei sendest.");
        }
      } finally {
        setUploading(false);
        setUploadHinweis(null);
        setPendingFiles((vorher) => {
          for (const p of vorher) {
            if (p.preview) URL.revokeObjectURL(p.preview);
          }
          return [];
        });
      }
    } else if (text) {
      try {
        const encrypted = await encryptMessage(text);
        await sendEncryptedMessage(encrypted, text, { replyToId: replyTo?.id });
      } catch (err) {
        console.error("Encryption failed:", err);
        if (err instanceof CryptoBrokenError) {
          alert("Dieses Gerät hat einen beschädigten Verschlüsselungs-Zustand. Bitte lade die Seite neu oder melde dich ab und wieder an — bis dahin können keine Nachrichten gesendet werden.");
        } else if (err instanceof RecipientNotReadyError) {
          alert("Empfänger ist noch nicht vollständig eingerichtet. Bitte später erneut versuchen.");
        } else if (err instanceof EmptyGroupError) {
          alert("Diese Gruppe hat noch keine anderen Mitglieder. Füge zuerst jemanden hinzu, bevor du schreibst.");
        } else {
          alert("Nachricht konnte nicht verschlüsselt werden. Bitte versuche es erneut.");
        }
        return;
      }
    } else {
      return;
    }

    setInputText("");
    setReplyTo(null);

    // Stop typing indicator
    ws.sendTyping(channelId, false);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  };

  /** Send a plain text message (used by VoiceChatMode — no E2EE needed for bot) */
  const sendPlainMessage = useCallback((text: string) => {
    if (!text.trim()) return;
    ws.sendMessage(channelId, text, "text");
  }, [channelId]);

  /** Encrypt result — either legacy single ciphertext or multi-device recipients */
  type EncryptResult =
    | { mode: "plaintext"; ciphertext: string }
    | { mode: "legacy"; ciphertext: string }
    | { mode: "multi-device"; recipients: MultiDeviceRecipient[]; partial?: boolean }
    | { mode: "group"; ciphertext: string };

  /**
   * Retry encryptForAllDevices on a transient failure of the /users/keys fetch
   * it does internally (429 shared rate-limit, 5xx, or a network error with no
   * status): 2 retries, ~1s then ~3s delay. Callers must NOT fall back to a
   * legacy single-device encryption when this still throws after retries — that
   * legacy path produces zero per-device envelopes for the peer's other devices
   * and has no debugPlaintext failsafe, silently converting a transient error
   * into a message the recipient can never decrypt (incident 2026-07).
   */
  const encryptFanoutWithRetry = async (
    recipientIds: string[],
    plaintext: string,
    meta: EncryptFanoutMeta,
  ): Promise<MultiDeviceRecipient[]> => {
    const retryDelaysMs = [1000, 3000];
    for (let attempt = 0; ; attempt++) {
      try {
        return await encryptForAllDevices(recipientIds, plaintext, meta);
      } catch (err) {
        if (err instanceof RecipientNotReadyError) throw err;
        const transient =
          (err instanceof ApiError && (err.status === 429 || err.status >= 500)) ||
          !(err instanceof ApiError);
        if (!transient || attempt >= retryDelaysMs.length) throw err;
        console.warn(
          `[Send] Fanout key fetch failed transiently (attempt ${attempt + 1}/${retryDelaysMs.length + 1}), retrying in ${retryDelaysMs[attempt]}ms:`,
          err,
        );
        await new Promise((resolve) => setTimeout(resolve, retryDelaysMs[attempt]));
      }
    }
  };

  /** Encrypt message using E2EE if available — multi-device for DM, sender key for group */
  const encryptMessage = async (plaintext: string): Promise<EncryptResult> => {

    if (!e2eeReady) throw new Error("E2EE not initialized — cannot send unencrypted");

    if (channel?.type === "dm") {
      // Encrypt for ALL other members (not just one), so both humans and bots receive the message
      const otherMembers = channel.members?.filter((m) => m.userId !== currentUserId) ?? [];
      if (otherMembers.length > 0) {
        const allOtherMembersAreBots = otherMembers.every((m) => m.isBot);
        // Multi-device path: encrypt for all devices of ALL recipients (+ own other devices)
        const recipientIds = otherMembers.map((m) => m.userId);
        const meta: EncryptFanoutMeta = { peerDevicesSeen: 0, peerEnvelopesProduced: 0, skipped: [] };
        // No legacy single-device fallback here on failure — see
        // encryptFanoutWithRetry's doc comment. A thrown error (RecipientNotReadyError
        // or an exhausted/non-transient fanout failure) propagates to handleSend,
        // which surfaces it to the user and preserves the typed text.
        const recipients = await encryptFanoutWithRetry(recipientIds, plaintext, meta);
        // Check if any recipient device belongs to a peer (not just self-encrypted)
        const hasPeerRecipient = recipients.some(r => {
          try {
            const env = JSON.parse(r.ciphertext);
            return !env.selfEncrypted;
          } catch { return true; }
        });
        if (recipients.length > 0 && hasPeerRecipient) {
          // partial=true means at least one server-known device couldn't be
          // reached this attempt; sendEncryptedMessage then drops the device-set
          // hash and lets async backfill / uploadMessageBackup heal the gaps.
          // ownDeviceFailed: own-device retry failed definitively → also partial so
          // the X-Expected-Devices hash check is skipped (avoids 409 → abort loop).
          return { mode: "multi-device", recipients, partial: meta.skipped.length > 0 || !!meta.ownDeviceFailed };
        }
        // No peer envelopes produced this attempt.
        if (allOtherMembersAreBots) {
          // Bot-only DM without E2EE — legitimate plaintext.
          return { mode: "plaintext", ciphertext: plaintext };
        }
        // Fix 1: only surface "recipient not set up" when the peer has ZERO
        // devices registered server-side. If they DO have devices but we just
        // couldn't reach any right now, do NOT hard-block: send our (self)
        // envelopes — never plaintext — and let receiver-side recovery + async
        // backfill deliver once the peer device is reachable again.
        if (meta.peerDevicesSeen > 0 && recipients.length > 0) {
          return { mode: "multi-device", recipients, partial: true };
        }
        throw new RecipientNotReadyError(
          otherMembers.filter((m) => !m.isBot).map((m) => m.userId),
        );
      }
    } else if (channel?.type === "group") {
      // Fetch a fresh member list from the server immediately before encryption.
      // The cached `channel.members` can lag behind if a `member_added` WS event
      // was missed (e.g. tab was backgrounded, reconnect raced the event, or a
      // member joined before this client subscribed to the channel). Sending with
      // a stale list silently drops the missing recipient from sender-key
      // distribution — they never get a pending envelope and can never decrypt
      // future group messages until the sender reloads.
      let memberIds: string[];
      try {
        const fresh = await api.get<{ members: Array<{ userId: string }> }>(
          `/channels/${channelId}`,
        );
        memberIds = (fresh.members ?? [])
          .filter((m) => m.userId !== currentUserId)
          .map((m) => m.userId);
      } catch {
        // Network blip — fall back to cached list rather than refuse to send.
        memberIds = channel.members
          ?.filter((m) => m.userId !== currentUserId)
          .map((m) => m.userId) ?? [];
      }
      if (memberIds.length > 0) {
        const ciphertext = await encryptForGroup(channelId, memberIds, plaintext);
        return { mode: "group", ciphertext };
      }
      // Solo group (only the sender): nothing to encrypt to. Distinct from
      // RecipientNotReadyError because the fix is "add members", not "wait".
      throw new EmptyGroupError(channelId);
    }

    throw new Error("No encryption path available — refusing to send plaintext");
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
      return;
    }

    if (e.key === "Escape" && (editingMessage || replyTo)) {
      setEditingMessage(null);
      setReplyTo(null);
      setInputText("");
      return;
    }

    // Send typing indicator (debounced)
    if (!typingTimeoutRef.current) {
      ws.sendTyping(channelId, true);
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      ws.sendTyping(channelId, false);
      typingTimeoutRef.current = null;
    }, 3000);
  };

  /** KOL-108: nimmt beliebig viele Dateien an, benennt was nicht passt. */
  const dateienUebernehmen = (roh: FileList | File[] | null | undefined) => {
    const neue = roh ? Array.from(roh) : [];
    if (neue.length === 0) return;

    setPendingFiles((vorhanden) => {
      const { angenommen, abgelehnt } = dateienAnnehmen(vorhanden, neue);
      const meldung = ablehnungsText(abgelehnt);
      if (meldung) alert(meldung);
      if (angenommen.length === 0) return vorhanden;
      return [
        ...vorhanden,
        ...angenommen.map((file) => ({
          file,
          preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
        })),
      ];
    });
    inputRef.current?.focus();
  };

  // handleDrop und der Paste-Listener sind mit [] memoisiert; ohne Ref würden
  // sie eine veraltete Fassung von dateienUebernehmen festhalten und beim
  // zweiten Ablegen die bereits gewählten Dateien überschreiben.
  const dateienUebernehmenRef = useRef(dateienUebernehmen);
  dateienUebernehmenRef.current = dateienUebernehmen;

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    dateienUebernehmen(e.target.files);
    e.target.value = "";
  };

  const cancelFile = (index: number) => {
    setPendingFiles((vorhanden) => {
      const weg = vorhanden[index];
      if (weg?.preview) URL.revokeObjectURL(weg.preview);
      return vorhanden.filter((_, i) => i !== index);
    });
  };

  // ── Drag & Drop ──
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes("Files")) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    dragCounterRef.current = 0;

    dateienUebernehmenRef.current(e.dataTransfer.files);
  }, []);

  // ── Paste (Ctrl+V) file support ──
  // Listens at document level so paste works even when the textarea isn't focused
  // (matches behavior of Slack / WhatsApp Web).

  useEffect(() => {
    const onDocPaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null;
      const cd = e.clipboardData;

      // Diagnostic logging — remove once paste is confirmed working
      console.log("[paste]", {
        target: target?.tagName,
        types: cd ? Array.from(cd.types) : null,
        itemsCount: cd?.items?.length ?? 0,
        items: cd
          ? Array.from(cd.items).map((it) => ({ kind: it.kind, type: it.type }))
          : null,
        filesCount: cd?.files?.length ?? 0,
        files: cd
          ? Array.from(cd.files).map((f) => ({ name: f.name, type: f.type, size: f.size }))
          : null,
      });

      if (target) {
        const tag = target.tagName;
        const isOtherInput =
          (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable) &&
          target !== inputRef.current;
        if (isOtherInput) return;
      }

      let file: File | null = null;

      // 1. Try clipboardData.items (most reliable in Chromium)
      const items = cd?.items;
      if (items) {
        for (const item of Array.from(items)) {
          if (item.kind === "file") {
            file = item.getAsFile();
            if (file) break;
          }
        }
      }

      // 2. Fallback: clipboardData.files (sometimes populated when items isn't)
      if (!file) {
        const files = cd?.files;
        if (files && files.length > 0) file = files[0];
      }

      // 3. Fallback: scan items for image/* MIME types via getAsFile
      //    (Firefox on Linux can list image types but kind != "file")
      if (!file && items) {
        for (const item of Array.from(items)) {
          if (item.type.startsWith("image/")) {
            const f = item.getAsFile();
            if (f) {
              file = f;
              break;
            }
          }
        }
      }

      if (!file) {
        console.log("[paste] no file extracted from clipboard");
        return;
      }

      console.log("[paste] extracted file", { name: file.name, type: file.type, size: file.size });

      e.preventDefault();
      dateienUebernehmenRef.current([file]);
    };

    document.addEventListener("paste", onDocPaste);
    return () => document.removeEventListener("paste", onDocPaste);
  }, []);

  // ── Emoji Picker ──

  const handleEmojiSelect = (emoji: string) => {
    const textarea = inputRef.current;
    if (textarea) {
      const start = textarea.selectionStart ?? inputText.length;
      const end = textarea.selectionEnd ?? inputText.length;
      const newText = inputText.slice(0, start) + emoji + inputText.slice(end);
      setInputText(newText);

      // Restore cursor position after emoji
      requestAnimationFrame(() => {
        const newPos = start + emoji.length;
        textarea.setSelectionRange(newPos, newPos);
        textarea.focus();
      });
    } else {
      setInputText((prev) => prev + emoji);
    }
    setShowEmojiPicker(false);
  };

  // ── Context Menu ──

  const handleContextMenu = (e: ReactMouseEvent, message: Message) => {
    e.preventDefault();
    menuPosRef.current = { x: e.clientX, y: e.clientY };
    setContextMenu({ x: e.clientX, y: e.clientY, message });
  };

  // Delete confirmation dialog
  const [deleteConfirm, setDeleteConfirm] = useState<Message | null>(null);

  const handleDeleteConfirmed = useCallback(() => {
    if (!deleteConfirm) return;
    // Optimistic: sofort aus UI entfernen
    removeMessage(deleteConfirm.id, channelId);
    ws.send({
      type: "delete_message",
      channelId,
      messageId: deleteConfirm.id,
    });
    setDeleteConfirm(null);
  }, [deleteConfirm, channelId, removeMessage]);

  const handleExportFrom = useCallback(async (fromId: string) => {
    const start = messages.findIndex((m) => m.id === fromId);
    if (start === -1) return;
    const slice = messages.slice(start);
    await exportConversationAsHtml(slice, {
      channelName: channel?.name ?? "Unterhaltung",
      currentUserName: user?.displayName ?? "Unbekannt",
      ownUserId: currentUserId,
    });
  }, [messages, channel, user, currentUserId]);

  const getContextMenuActions = useCallback((msg: Message): ContextMenuAction[] => {
    const isOwn = msg.senderId === currentUserId;
    const plaintext = safeMessageText(msg);
    const actions: ContextMenuAction[] = [];

    actions.push({
      label: "Reagieren",
      icon: <Smile size={16} />,
      action: () => {
        setReactionPicker({ x: menuPosRef.current.x, y: menuPosRef.current.y, message: msg });
      },
    });

    actions.push({
      label: "Antworten",
      icon: <Reply size={16} />,
      action: () => {
        setReplyTo(msg);
        inputRef.current?.focus();
      },
    });

    actions.push({
      label: "Kopieren",
      icon: <Copy size={16} />,
      action: () => {
        navigator.clipboard.writeText(plaintext).catch(() => {});
      },
    });

    actions.push({
      label: "Weiterleiten",
      icon: <Forward size={16} />,
      action: () => {
        setForwardMessage(msg);
      },
    });

    if (isOwn) {
      actions.push({
        label: "Bearbeiten",
        icon: <Pencil size={16} />,
        action: () => {
          setEditingMessage(msg);
          setInputText(plaintext);
          inputRef.current?.focus();
        },
      });

      actions.push({
        label: "Löschen",
        icon: <Trash2 size={16} />,
        danger: true,
        action: () => {
          setDeleteConfirm(msg);
        },
      });
    }

    if (isAdmin) {
      actions.push({
        label: "Exportieren (ab hier)",
        icon: <Download size={16} />,
        action: () => { handleExportFrom(msg.id).catch((err) => console.error("[export]", err)); },
      });
    }

    return actions;
  }, [currentUserId, channelId, isAdmin, handleExportFrom]);

  // ── Long Press ──

  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTouchStart = (e: React.TouchEvent, message: Message) => {
    const touch = e.touches[0];
    if (!touch) return;
    const x = touch.clientX;
    const y = touch.clientY;

    longPressTimerRef.current = setTimeout(() => {
      menuPosRef.current = { x, y };
      setContextMenu({ x, y, message });
    }, 500);
  };

  const handleTouchEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // ── Reactions ──

  const handleReactionToggle = useCallback((messageId: string, emoji: string) => {
    ws.send({
      type: "reaction",
      channelId,
      messageId,
      emoji,
    });
  }, [channelId]);

  const dmPartner = channel?.type === "dm"
    ? channel?.members?.find((m) => m.userId !== currentUserId)
    : null;
  const channelName = (channel?.type === "dm" && dmPartner?.displayName)
    ? dmPartner.displayName
    : channel?.name ?? "Chat";
  const partnerOnline = dmPartner ? onlineUsers.has(dmPartner.userId) : false;
  const isBotChannel = dmPartner ? channel?.members?.some(m => m.userId === dmPartner.userId && m.isBot) : false;

  const headerSubtitle = useMemo(() => {
    if (channel?.type === "dm") {
      if (isBotChannel) return "🤖 Bot";
      if (partnerOnline) return "🟢 Online";
      // Show "Zuletzt online" timestamp
      const lastSeen = dmPartner ? lastSeenMap.get(dmPartner.userId) : undefined;
      if (lastSeen) {
        const d = new Date(lastSeen);
        const now = new Date();
        const diffMin = Math.floor((now.getTime() - d.getTime()) / 60000);
        if (diffMin < 1) return "Gerade eben online";
        if (diffMin < 60) return `Zuletzt online vor ${diffMin} Min.`;
        if (diffMin < 1440) {
          return `Zuletzt online um ${d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`;
        }
        return `Zuletzt online ${d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}`;
      }
      return "Offline";
    }
    const memberCount = channel?.members?.length ?? 0;
    const onlineCount = channel?.members?.filter(m => onlineUsers.has(m.userId)).length ?? 0;
    return `${memberCount} Mitglieder${onlineCount > 0 ? ` · ${onlineCount} online` : ""}`;
  }, [channel?.type, channel?.members, isBotChannel, partnerOnline, onlineUsers, lastSeenMap, dmPartner]);

  const searchMatchCount = useMemo(() => {
    if (!searchQuery) return 0;
    return messages.filter(m =>
      safeMessageText(m).toLowerCase().includes(searchQuery.toLowerCase())
    ).length;
  }, [messages, searchQuery]);

  // Pre-compute bot member IDs as a Set — avoids .some() on every message render
  const botMemberIds = useMemo(() => {
    const ids = new Set<string>();
    for (const m of channel?.members ?? []) {
      if (m.isBot) ids.add(m.userId);
    }
    return ids;
  }, [channel?.members]);

  // Pre-compute the "latest read timestamp" per other-user — avoids Array.from().some() inside map()
  const latestReadTimestamp = useMemo(() => {
    if (!channelReceipts) return null;
    let latest: number | null = null;
    for (const [uid, readMsgId] of channelReceipts.entries()) {
      if (uid === currentUserId) continue;
      const readMsg = messages.find(m => m.id === readMsgId);
      if (!readMsg) {
        // Receipt exists but message not in view — treat as "everything read"
        return Infinity;
      }
      const ts = new Date(readMsg.createdAt).getTime();
      if (latest === null || ts > latest) latest = ts;
    }
    return latest;
  }, [channelReceipts, messages, currentUserId]);

  return (
    <div className={styles.chatWrapper}>
      <div
        className={`${styles.chatView} ${isDragOver ? styles.dragOver : ""}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Chat Header */}
        <div className={styles.header}>
          {onBack && (
            <button
              className={styles.backBtn}
              onClick={onBack}
              aria-label="Zurück"
            >
              <ChevronLeft size={18} />
            </button>
          )}
          <Avatar
            name={channelName}
            size="sm"
            online={channel?.type === "dm" ? partnerOnline : undefined}
            isBot={!!isBotChannel}
          />
          <div className={styles.headerInfo}>
            <h3 className={styles.headerName}>{channelName}</h3>
            <span className={styles.headerStatus}>
              {headerSubtitle}
            </span>
          </div>
          <div className={styles.headerActions}>
            {isBotChannel && (
              <button
                className={styles.headerBtn}
                aria-label="Voice Chat"
                onClick={() => {
                  // Unlock TTS on user gesture (required for iOS Safari)
                  if ("speechSynthesis" in window) {
                    const s = new SpeechSynthesisUtterance("");
                    s.volume = 0;
                    s.rate = 10;
                    speechSynthesis.speak(s);
                  }
                  setShowVoiceChat(true);
                }}
                title="Voice Chat mit Lara"
              >
                <Phone size={18} />
              </button>
            )}
            <button
              className={styles.headerBtn}
              aria-label="Hilfe"
              onClick={() => setShowHelp(true)}
            ><HelpCircle size={18} /></button>
            <button
              className={`${styles.headerBtn} ${showSearch ? styles.headerBtnActive : ""}`}
              aria-label="Suche"
              onClick={() => {
                setShowSearch(!showSearch);
                if (!showSearch) setTimeout(() => searchInputRef.current?.focus(), 100);
                else setSearchQuery("");
              }}
            ><Search size={18} /></button>
            <button
              className={`${styles.headerBtn} ${showInfo ? styles.headerBtnActive : ""}`}
              aria-label="Info"
              onClick={() => setShowInfo(!showInfo)}
            >
              <Info size={18} />
            </button>
          </div>
        </div>

        {/* Reconnect Banner */}
        {banner === "disconnected" && (
          <div className={styles.reconnectBanner}>
            ⚡ Eingeschränkter Modus — Nachrichten werden verzögert aktualisiert
          </div>
        )}
        {banner === "reconnected" && (
          <div className={`${styles.reconnectBanner} ${styles.reconnectSuccess}`}>
            ✓ Verbunden
          </div>
        )}

        {/* Search Bar */}
        {showSearch && (
          <SearchBar
            channelId={channelId}
            searchInputRef={searchInputRef}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            matchCount={searchMatchCount}
            onClose={() => {
              setShowSearch(false);
              setSearchQuery("");
            }}
            onResultClick={(messageId) => {
              // Disable sticky so the ResizeObserver doesn't snap back to the bottom
              // (handleScroll will re-enable it if the user scrolls back down).
              stickToBottomRef.current = false;
              // Try to scroll to message and highlight it
              const el = document.getElementById(`msg-${messageId}`);
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                el.classList.add(styles.searchHighlightFlash);
                setTimeout(() => el.classList.remove(styles.searchHighlightFlash), 2000);
              }
            }}
          />
        )}

        {/* Drag & Drop Overlay */}
        {isDragOver && (
          <div className={styles.dropOverlay}>
            <div className={styles.dropContent}>
              <span className={styles.dropIcon}><Paperclip size={48} /></span>
              <span className={styles.dropText}>Datei hier ablegen</span>
            </div>
          </div>
        )}

        {/* Messages */}
        <div
          ref={messagesContainerRef}
          className={styles.messages}
          onScroll={handleScroll}
        >
        <div ref={messagesContentRef} className={styles.messagesContent}>
          {/* Loading indicator for older messages */}
          {isLoadingMessages && messages.length > 0 && (
            <div className={styles.loadingMore}>
              <Loader2 size={16} className="spin" />
              <span>Lade ältere Nachrichten...</span>
            </div>
          )}

          {/* Initial-load spinner: store is empty while the first page fetches +
              decrypts. Without this, the "Noch keine Nachrichten" empty state
              below would show during the whole load (misleading on busy channels). */}
          {isLoadingMessages && messages.length === 0 && (
            <div className={styles.loadingMore}>
              <Loader2 size={16} className="spin" />
              <span>Lade Nachrichten...</span>
            </div>
          )}

          {messages.length === 0 && !isLoadingMessages && (
            <div className={styles.emptyChat}>
              <span className={styles.emptyChatIcon}>👋</span>
              <p>Noch keine Nachrichten. Schreib die erste!</p>
            </div>
          )}

          {messages.map((msg, i) => {
            const isOwn = msg.senderId === currentUserId;
            const prevMsg = i > 0 ? messages[i - 1] : undefined;

            // Message grouping: same sender + less than 2 minutes apart
            const isGrouped = prevMsg
              && prevMsg.senderId === msg.senderId
              && !prevMsg.deletedAt
              && (new Date(msg.createdAt).getTime() - new Date(prevMsg.createdAt).getTime()) < 120_000;

            const showAvatar = !isOwn && !isGrouped;

            const showDate = i === 0 || !isSameDay(
              new Date(messages[i - 1]!.createdAt),
              new Date(msg.createdAt),
            );

            // Date separator breaks grouping
            const grouped = isGrouped && !showDate;

            // Find reply-to message
            const replyMsg = msg.replyToId
              ? messages.find((m) => m.id === msg.replyToId)
              : null;

            const isSearchMatch = searchQuery && safeMessageText(msg).toLowerCase().includes(searchQuery.toLowerCase());

            return (
              <Fragment key={msg.id}>
                {showDate && (
                  <div className={styles.dateSeparator}>
                    <span>{formatDate(new Date(msg.createdAt))}</span>
                  </div>
                )}
              <div id={`msg-${msg.id}`} className={`${styles.messageWrapper} ${isOwn ? styles.messageWrapperOwn : ""} ${isSearchMatch ? styles.searchHighlight : ""} ${grouped ? styles.grouped : ""}`}>
                <MessageBubble
                  message={msg}
                  isOwn={isOwn}
                  showAvatar={showAvatar}
                  grouped={grouped}
                  isBotMessage={botMemberIds.has(msg.senderId)}
                  isRead={isOwn && latestReadTimestamp !== null
                    ? latestReadTimestamp === Infinity || new Date(msg.createdAt).getTime() <= latestReadTimestamp
                    : false}
                  replyTo={replyMsg}

                  onContextMenu={(e) => handleContextMenu(e, msg)}
                  onTouchStart={(e) => handleTouchStart(e, msg)}
                  onTouchEnd={handleTouchEnd}
                  onTouchMove={handleTouchEnd}
                  onReactionToggle={(emoji) => handleReactionToggle(msg.id, emoji)}
                  onImageClick={(blobUrl) => setLightboxUrl(blobUrl)}
                  onMenuOpen={(x, y) => { menuPosRef.current = { x, y }; setContextMenu({ x, y, message: msg }); }}
                />
              </div>
              </Fragment>
            );
          })}

          {/* Bot Streaming Bubble — visible while streaming AND while waiting for the
              persisted message after stream_end (F11/F16).
              - isStreaming → blinking cursor.
              - finalizing → spinner + "Antwort wird abgeschlossen…" hint, content faded.
              - failed → warning + retry hint instead of indefinite spinner.
              When no stream bubble: fall through to bot_typing indicator below. */}
          {(() => {
            const stream = botStreams.get(channelId);
            if (!stream) return null;
            const botMember = channel?.members?.find(m => m.userId === stream.botUserId);
            const botName = botMember?.displayName ?? "Bot";
            const contentClass = stream.finalizing || stream.failed
              ? `${styles.streamingText} ${styles.streamingTextFaded}`
              : styles.streamingText;
            return (
              <div className={styles.streamingBubble}>
                <Avatar name={botName} size="xs" isBot />
                <div className={styles.streamingContent}>
                  <span className={styles.senderName}>{botName}</span>
                  <div className={contentClass}>
                    {stream.content ? <MessageMarkdown text={stream.content} /> : (
                      <span className={styles.streamingPlaceholder}>
                        <span className={styles.typingDots}><span /><span /><span /></span>
                      </span>
                    )}
                    {stream.isStreaming && <span className={styles.streamingCursor}>▊</span>}
                  </div>
                  {stream.finalizing && !stream.failed && (
                    <div className={styles.streamingHint}>
                      <span className={styles.typingDots}><span /><span /><span /></span>
                      <span>Antwort wird abgeschlossen…</span>
                    </div>
                  )}
                  {stream.failed && (
                    <div className={styles.streamingError}>
                      Keine Antwort empfangen. Bitte erneut versuchen.
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* F16 Fix 2: bot_typing indicator — shows when bot is working but no
              stream bubble is active (e.g. before first chunk, or after a stream
              ended but the persisted msg is still being prepared on the server). */}
          {(() => {
            if (botStreams.get(channelId)) return null;
            const typing = botTyping.get(channelId);
            // KOL-81: ephemeral activity ticker — shows the bot's current action
            // ("🔎 Webrecherche: …") inside the typing bubble; falls back to dots.
            const activity = botActivity.get(channelId);
            if (!typing && !activity) return null;
            const botUserId = typing?.botUserId ?? activity?.botUserId;
            const botMember = channel?.members?.find(m => m.userId === botUserId);
            const botName = botMember?.displayName ?? "Bot";
            return (
              <div className={styles.streamingBubble}>
                <Avatar name={botName} size="xs" isBot />
                <div className={styles.streamingContent}>
                  <span className={styles.senderName}>{botName}</span>
                  <div className={styles.streamingText}>
                    <span className={styles.streamingPlaceholder}>
                      {activity ? (
                        <span className={styles.botActivityText}>{activity.text}</span>
                      ) : (
                        <span className={styles.typingDots}><span /><span /><span /></span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Typing Indicator — nur andere User anzeigen */}
          {(() => {
            const othersTyping = typing.filter(t => t.userId !== currentUserId);
            if (othersTyping.length === 0) return null;
            let label: string;
            if (othersTyping.length === 1) {
              label = `${othersTyping[0]!.displayName} tippt...`;
            } else if (othersTyping.length === 2) {
              label = `${othersTyping[0]!.displayName} und ${othersTyping[1]!.displayName} tippen...`;
            } else {
              label = "Mehrere tippen...";
            }
            return (
              <div className={styles.typing}>
                <span className={styles.typingDots}>
                  <span /><span /><span />
                </span>
                <span className={styles.typingText}>{label}</span>
              </div>
            );
          })()}

          <div ref={messagesEndRef} />
        </div>
        </div>

        {/* New Message Button */}
        {showNewMessageBtn && (
          <button
            className={styles.newMessageBtn}
            onClick={() => scrollToBottom()}
          >
            ↓
          </button>
        )}

        {channel?.type === "group" &&
          (channel.members?.filter((m) => m.userId !== currentUserId).length ?? 0) === 0 && (
            <div className={styles.emptyGroupHint}>
              Diese Gruppe hat noch keine anderen Mitglieder. Füge zuerst jemanden hinzu, bevor du schreibst.
            </div>
          )}

        <MessageInput
          channelId={channelId}
          inputText={inputText}
          onInputChange={setInputText}
          onSubmit={handleSend}
          onKeyDown={handleKeyDown}
          onFileSelect={handleFileSelect}
          onEmojiSelect={handleEmojiSelect}
          showEmojiPicker={showEmojiPicker}
          onToggleEmojiPicker={() => setShowEmojiPicker(!showEmojiPicker)}
          uploading={uploading}
          editingMessage={editingMessage}
          replyTo={replyTo}
          pendingFiles={pendingFiles}
          uploadHinweis={uploadHinweis}
          onCancelFile={cancelFile}
          onCancelEdit={() => {
            setEditingMessage(null);
            setInputText("");
          }}
          onCancelReply={() => setReplyTo(null)}
          inputRef={inputRef}
          fileInputRef={fileInputRef}
          onSendVoice={async (attachment: string) => {
            try {
              const encrypted = await encryptMessage(attachment);
              await sendEncryptedMessage(encrypted, attachment, { contentType: "e2ee" });
            } catch (err) {
              console.error("Voice encrypt/send failed:", err);
              if (err instanceof CryptoBrokenError) {
                // Broken local crypto — must NOT fall back to plaintext.
                alert("Dieses Gerät hat einen beschädigten Verschlüsselungs-Zustand. Bitte lade die Seite neu oder melde dich ab und wieder an — bis dahin können keine Nachrichten gesendet werden.");
                return;
              }
              if (err instanceof RecipientNotReadyError) {
                alert("Empfänger ist noch nicht vollständig eingerichtet. Bitte später erneut versuchen.");
                return;
              }
              ws.sendMessage(channelId, attachment);
            }
          }}
        />
      </div>

      {/* Channel Info Panel — rendered alongside chat */}
      {showInfo && (
        <ChannelInfo
          channelId={channelId}
          currentUserId={currentUserId}
          onClose={() => setShowInfo(false)}
          onLeaveChannel={() => {
            setShowInfo(false);
            // Reload channels and deselect
            useChat.getState().loadChannels();
            useChat.getState().setActiveChannel("");
          }}
        />
      )}

      {/* Forward Dialog */}
      {forwardMessage && (
        <ForwardDialog
          forwardMessage={forwardMessage}
          channels={channels}
          currentChannelId={channelId}
          onClose={() => setForwardMessage(null)}
        />
      )}

      {/* Image Lightbox */}
      {lightboxUrl && (
        <ImageLightbox src={lightboxUrl} onClose={() => setLightboxUrl(null)} />
      )}

      {/* Help Dialog */}
      {showHelp && <HelpDialog onClose={() => setShowHelp(false)} />}

      {/* Context Menu */}
      {contextMenu && (
        <MessageContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          actions={getContextMenuActions(contextMenu.message)}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Reaction Picker */}
      {reactionPicker && (
        <div style={{ position: "fixed", top: reactionPicker.y, left: reactionPicker.x, zIndex: 1000 }}>
          <Suspense fallback={null}>
            <EmojiPicker
              onSelect={(emoji) => {
                handleReactionToggle(reactionPicker.message.id, emoji);
                setReactionPicker(null);
              }}
              onClose={() => setReactionPicker(null)}
            />
          </Suspense>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className={styles.dialogOverlay} onClick={() => setDeleteConfirm(null)}>
          <div className={styles.dialogBox} onClick={(e) => e.stopPropagation()}>
            <h4 className={styles.dialogTitle}>Nachricht löschen</h4>
            <p className={styles.dialogText}>Nachricht wirklich löschen? Das kann nicht rückgängig gemacht werden.</p>
            <div className={styles.dialogActions}>
              <button
                className={styles.dialogCancel}
                onClick={() => setDeleteConfirm(null)}
              >
                Abbrechen
              </button>
              <button
                className={styles.dialogConfirm}
                onClick={handleDeleteConfirmed}
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Voice Chat Mode Overlay */}
      {showVoiceChat && isBotChannel && (
        <VoiceChatMode
          channelId={channelId}
          currentUserId={currentUserId}
          botName={dmPartner?.displayName ?? "Lara"}
          onClose={() => setShowVoiceChat(false)}
          onSendMessage={sendPlainMessage}
        />
      )}
    </div>
  );
}

// ── Helpers ──

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function formatDate(date: Date): string {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (isSameDay(date, today)) return "Heute";
  if (isSameDay(date, yesterday)) return "Gestern";

  return date.toLocaleDateString("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: date.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
  });
}
